import { useEffect, useId, useRef, type FormEvent } from "react";
import { LockKeyhole, ShieldCheck } from "lucide-react";

import { PrimaryCommandButton } from "@/ui";

export interface ParentPinGateProps {
  mode: "setup" | "locked";
  pin: string;
  confirmationPin: string;
  busy: boolean;
  error?: string;
  onPinChange: (pin: string) => void;
  onConfirmationPinChange: (pin: string) => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
}

export function ParentPinGate({
  mode,
  pin,
  confirmationPin,
  busy,
  error,
  onPinChange,
  onConfirmationPinChange,
  onSubmit
}: ParentPinGateProps) {
  const isSetup = mode === "setup";
  const errorId = useId();
  const pinInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    pinInputRef.current?.focus();
  }, [mode]);

  return (
    <section
      aria-busy={busy}
      aria-labelledby="parent-pin-title"
      className="parent-center__gate"
    >
      <span aria-hidden="true" className="parent-center__gate-icon">
        {isSetup ? <ShieldCheck /> : <LockKeyhole />}
      </span>
      <p className="parent-center__eyebrow">家长空间</p>
      <h1 id="parent-pin-title">
        {isSetup ? "设置家长密码" : "请输入家长密码"}
      </h1>
      <form className="parent-center__pin-form" onSubmit={onSubmit}>
        <label htmlFor="parent-pin">
          {isSetup ? "设置 4 位数字密码" : "4 位数字密码"}
        </label>
        <input
          aria-describedby={error ? errorId : undefined}
          aria-invalid={error ? "true" : undefined}
          autoComplete="off"
          disabled={busy}
          enterKeyHint={isSetup ? "next" : "done"}
          id="parent-pin"
          inputMode="numeric"
          maxLength={4}
          onChange={(event) => onPinChange(event.currentTarget.value)}
          pattern="[0-9]{4}"
          ref={pinInputRef}
          required
          type="password"
          value={pin}
        />
        {isSetup ? (
          <>
            <label htmlFor="parent-pin-confirmation">
              再输入一次
            </label>
            <input
              aria-describedby={error ? errorId : undefined}
              aria-invalid={error ? "true" : undefined}
              autoComplete="off"
              disabled={busy}
              enterKeyHint="done"
              id="parent-pin-confirmation"
              inputMode="numeric"
              maxLength={4}
              onChange={(event) =>
                onConfirmationPinChange(event.currentTarget.value)
              }
              pattern="[0-9]{4}"
              required
              type="password"
              value={confirmationPin}
            />
          </>
        ) : null}
        {error ? (
          <p className="parent-center__message parent-center__message--error" id={errorId} role="alert">
            {error}
          </p>
        ) : null}
        <PrimaryCommandButton
          disabled={
            busy ||
            pin.length !== 4 ||
            (isSetup && confirmationPin.length !== 4)
          }
          icon={isSetup ? ShieldCheck : LockKeyhole}
          type="submit"
        >
          {busy ? "请稍候" : isSetup ? "设置并进入" : "进入家长空间"}
        </PrimaryCommandButton>
      </form>
    </section>
  );
}
